import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Avatar,
  Chip,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Slide,
  useScrollTrigger,
} from '@mui/material';
import {
  SportsCricket,
  Dashboard,
  People,
  Groups,
  TrendingUp,
  Logout,
  KeyboardArrowDown,
  Insights,
  CurrencyRupee,
  Science,
  FactCheck,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// Hides the app bar when scrolling down and slides it back in when scrolling up.
const HideOnScroll: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const trigger = useScrollTrigger();
  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
};

// Every signed-in user is shown as the shared RR Analytics account; the login id is never displayed.
const ACCOUNT_NAME = 'RR Analytics';
const ACCOUNT_INITIALS = 'RR';

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  type NavLeaf = { path: string; label: string; icon: React.ReactNode; beta?: boolean };
  type NavItem = NavLeaf | { label: string; icon: React.ReactNode; children: NavLeaf[] };
  const menuItems: NavItem[] = [
    // Dashboard and Players - Deactivated for now
    // { path: '/', label: 'Dashboard', icon: <Dashboard /> },
    // { path: '/players', label: 'Players', icon: <People /> },
    { path: '/projections', label: 'Projections', icon: <Insights /> },
    {
      label: 'Playgrounds',
      icon: <Science />,
      children: [
        { path: '/lineup-playground', label: 'Lineup Playground', icon: <SportsCricket /> },
        { path: '/salary-playground', label: 'Salary Playground', icon: <CurrencyRupee /> },
      ],
    },
    {
      label: 'Trades',
      icon: <TrendingUp />,
      children: [
        { path: '/trade-analysis', label: 'Trade Machine', icon: <TrendingUp /> },
        { path: '/trade-scorecard', label: 'Trade Scorecard', icon: <FactCheck />, beta: true },
      ],
    },
    { path: '/lineups', label: 'Lineups', icon: <Groups /> },
  ];
  const [groupAnchor, setGroupAnchor] = useState<null | HTMLElement>(null);
  const [groupLabel, setGroupLabel] = useState<string>('');

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const handleLogout = () => {
    setAnchorEl(null);
    logout();
    navigate('/login');
  };

  return (
    <>
      <HideOnScroll>
        <AppBar position="fixed" elevation={2}>
          <Toolbar>
        <Box
          onClick={() => navigate('/lineups')}
          role="button"
          aria-label="Rajasthan Royals — go to Lineups"
          sx={{
            width: 46,
            height: 46,
            mr: 1.5,
            cursor: 'pointer',
            borderRadius: '50%',
            bgcolor: 'common.white',
            boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Box
            component="img"
            src="/static/metalogo/rr-crest.png"
            alt="Rajasthan Royals"
            sx={{ height: '78%', width: 'auto', maxWidth: '82%', objectFit: 'contain', display: 'block' }}
          />
        </Box>

        <Typography
          variant="h6"
          component="div"
          sx={{ flexGrow: 0, mr: 4, fontWeight: 700, letterSpacing: 0.3 }}
        >
         RR SquadLab
        </Typography>

        {/* Spacer pushes the menu tabs and user controls to the right */}
        <Box sx={{ flexGrow: 1 }} />

        <Box sx={{ flexGrow: 0, display: { xs: 'none', md: 'flex' }, mr: 2 }}>
          {menuItems.map((item) => {
            if ('children' in item) {
              const open = groupAnchor !== null && groupLabel === item.label;
              const active = item.children.some((c) => isActive(c.path));
              return (
                <React.Fragment key={item.label}>
                  <Button
                    color="inherit"
                    startIcon={item.icon}
                    endIcon={<KeyboardArrowDown sx={{ fontSize: 18, transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'none' }} />}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    onClick={(e) => {
                      setGroupAnchor(e.currentTarget);
                      setGroupLabel(item.label);
                    }}
                    sx={{
                      mx: 1,
                      backgroundColor: active ? 'rgba(255,255,255,0.1)' : 'transparent',
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
                    }}
                  >
                    {item.label}
                  </Button>
                  <Menu
                    anchorEl={groupAnchor}
                    open={open}
                    onClose={() => setGroupAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    slotProps={{ paper: { elevation: 3, sx: { mt: 1, minWidth: 220, borderRadius: 2 } } }}
                  >
                    {item.children.map((c) => (
                      <MenuItem
                        key={c.path}
                        selected={isActive(c.path)}
                        onClick={() => {
                          setGroupAnchor(null);
                          handleNavigation(c.path);
                        }}
                        sx={{ py: 1.1 }}
                      >
                        <ListItemIcon>{c.icon}</ListItemIcon>
                        <ListItemText primary={c.label} primaryTypographyProps={{ fontWeight: isActive(c.path) ? 700 : 500 }} />
                        {c.beta && <Chip label="Beta" size="small" variant="outlined" color="primary" sx={{ ml: 1, height: 18, fontSize: 10, fontWeight: 700 }} />}
                      </MenuItem>
                    ))}
                  </Menu>
                </React.Fragment>
              );
            }
            return (
              <Button
                key={item.path}
                color="inherit"
                startIcon={item.icon}
                onClick={() => handleNavigation(item.path)}
                sx={{
                  mx: 1,
                  backgroundColor: isActive(item.path) ? 'rgba(255,255,255,0.1)' : 'transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.1)',
                  },
                }}
              >
                {item.label}
              </Button>
            );
          })}
        </Box>

        <Box sx={{ flexGrow: 0, display: 'flex', alignItems: 'center' }}>
          <Tooltip title="Account">
            <Button
              onClick={(e) => setAnchorEl(e.currentTarget)}
              aria-haspopup="true"
              aria-expanded={menuOpen ? 'true' : undefined}
              sx={{
                color: 'white',
                textTransform: 'none',
                pl: 0.5,
                pr: { xs: 0.5, sm: 1.25 },
                py: 0.5,
                borderRadius: 999,
                gap: 1,
                border: '1px solid rgba(255,255,255,0.25)',
                backgroundColor: 'rgba(255,255,255,0.08)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.18)' },
              }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  fontSize: 14,
                  fontWeight: 700,
                  bgcolor: 'secondary.main',
                  color: 'white',
                }}
              >
                {ACCOUNT_INITIALS}
              </Avatar>
              <KeyboardArrowDown
                sx={{
                  fontSize: 18,
                  display: { xs: 'none', sm: 'block' },
                  transition: 'transform 0.2s',
                  transform: menuOpen ? 'rotate(180deg)' : 'none',
                }}
              />
            </Button>
          </Tooltip>

          <Menu
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{
              paper: {
                elevation: 3,
                sx: { mt: 1, minWidth: 240, borderRadius: 2, overflow: 'visible' },
              },
            }}
          >
            <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  fontSize: 16,
                  fontWeight: 700,
                  bgcolor: 'secondary.main',
                  color: 'white',
                }}
              >
                {ACCOUNT_INITIALS}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                  {ACCOUNT_NAME}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Signed in
                </Typography>
              </Box>
            </Box>
            <Divider />
            <MenuItem onClick={handleLogout} sx={{ py: 1.25, color: 'error.main' }}>
              <ListItemIcon sx={{ color: 'error.main' }}>
                <Logout fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Logout" primaryTypographyProps={{ fontWeight: 600 }} />
            </MenuItem>
          </Menu>
        </Box>
          </Toolbar>
        </AppBar>
      </HideOnScroll>
      {/* Spacer offsets the fixed AppBar so page content isn't hidden underneath */}
      <Toolbar />
    </>
  );
};

export default Navbar;
